import { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { getUserProfile, updateUserProfile, convertDbProfileToCamelCase } from '../../../services/profileService';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';

const PersonalInfoSection = ({ userRole }) => {
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    location: '',
    profilePhoto: ''
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadUserProfile();
  }, [user]);

  const loadUserProfile = async () => {
    if (!user?.id) return;
    
    try {
      setLoading(true);
      const profile = await getUserProfile(user?.id);
      const camelCaseProfile = convertDbProfileToCamelCase(profile);
      
      setFormData({
        fullName: camelCaseProfile?.fullName || '',
        email: camelCaseProfile?.email || '',
        phone: camelCaseProfile?.phone || '',
        location: camelCaseProfile?.location || '',
        profilePhoto: camelCaseProfile?.profilePhoto || ''
      });
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData?.fullName?.trim()) newErrors.fullName = 'Full name is required';
    if (!formData?.email?.trim()) newErrors.email = 'Email is required';
    if (!formData?.phone?.trim()) newErrors.phone = 'Phone number is required';
    if (!formData?.location?.trim()) newErrors.location = 'Location is required';
    
    const phoneRegex = /^\+91\s\d{5}\s\d{5}$/;
    if (formData?.phone && !phoneRegex?.test(formData?.phone)) {
      newErrors.phone = 'Phone must be in format: +91 XXXXX XXXXX';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSave = async () => {
    if (!validateForm() || !user?.id) return;
    
    try {
      setSaving(true);
      await updateUserProfile(user?.id, formData);
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving profile:', error);
      setErrors({ submit: error?.message || 'Failed to save profile' });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setErrors({});
    loadUserProfile();
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6 flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="User" size={20} color="var(--color-primary)" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">Personal Information</h2>
            <p className="text-sm text-muted-foreground">Update your personal details and profile photo</p>
          </div>
        </div>
        {!isEditing && (
          <Button
            variant="outline"
            size="sm"
            iconName="Edit2"
            iconPosition="left"
            onClick={() => setIsEditing(true)}
            disabled={loading}
          >
            Edit
          </Button>
        )}
      </div>
      {errors?.submit && (
        <div className="mb-4 p-3 bg-error/10 border border-error/20 rounded-lg flex items-start gap-2">
          <Icon name="AlertCircle" size={18} className="text-error flex-shrink-0 mt-0.5" />
          <p className="text-sm text-error">{errors?.submit}</p>
        </div>
      )}
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
          <div className="relative">
            <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-border">
              {formData?.profilePhoto ? (
                <Image
                  src={formData?.profilePhoto}
                  alt="Professional headshot of person with warm smile wearing formal attire against neutral background"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-muted flex items-center justify-center">
                  <Icon name="User" size={40} className="text-muted-foreground" />
                </div>
              )}
            </div>
            {isEditing && (
              <button className="absolute bottom-0 right-0 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center hover:bg-primary/90 transition-smooth">
                <Icon name="Camera" size={16} />
              </button>
            )}
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-medium text-foreground mb-1">Profile Photo</h3>
            <p className="text-xs text-muted-foreground mb-3">JPG, PNG or GIF. Max size 2MB. Recommended 400x400px</p>
            {isEditing && (
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" iconName="Upload">
                  Upload New
                </Button>
                <Button variant="ghost" size="sm" iconName="Trash2">
                  Remove
                </Button>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Full Name"
            name="fullName"
            type="text"
            value={formData?.fullName}
            onChange={handleInputChange}
            disabled={!isEditing}
            error={errors?.fullName}
            required
            placeholder="Enter your full name"
          />

          <Input
            label="Email Address"
            name="email"
            type="email"
            value={formData?.email}
            onChange={handleInputChange}
            disabled={!isEditing}
            error={errors?.email}
            required
            placeholder="your.email@example.com"
          />

          <Input
            label="Phone Number"
            name="phone"
            type="tel"
            value={formData?.phone}
            onChange={handleInputChange}
            disabled={!isEditing}
            error={errors?.phone}
            required
            placeholder="+91 XXXXX XXXXX"
            description="Format: +91 XXXXX XXXXX"
          />

          <Input
            label="Location"
            name="location"
            type="text"
            value={formData?.location}
            onChange={handleInputChange}
            disabled={!isEditing}
            error={errors?.location}
            required
            placeholder="City, State"
          />
        </div>

        {isEditing && (
          <div className="flex flex-wrap gap-3 pt-4 border-t border-border">
            <Button
              variant="default"
              iconName="Check"
              iconPosition="left"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
            <Button
              variant="outline"
              iconName="X"
              iconPosition="left"
              onClick={handleCancel}
              disabled={saving}
            >
              Cancel
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PersonalInfoSection;